// src/common.ts
import { z } from "zod";
var ApiErrorSchema = z.object({
  statusCode: z.number().int(),
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional()
  })
});
var API_ERROR_CODES = [
  "VALIDATION_FAILED",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "TOO_MANY_REQUESTS",
  "UPGRADE_REQUIRED",
  "INTERNAL_ERROR",
  // Comptes et sessions
  "ACCOUNT_SUSPENDED",
  "INVALID_REFRESH_TOKEN",
  "GUEST_SIGNUPS_CLOSED",
  "EMAIL_LOGIN_CLOSED",
  "BOT_CHECK_FAILED",
  "BOT_CHECK_UNAVAILABLE",
  "MAIL_UNAVAILABLE",
  // Code par e-mail
  "CODE_INVALID",
  "CODE_EXPIRED",
  "CODE_ATTEMPTS_EXCEEDED",
  "CODE_RESEND_TOO_SOON",
  "EMAIL_RATE_LIMITED",
  "CHOICE_EXPIRED",
  // Sauvegarde de la station
  "STATION_TAKEN_OVER",
  "REVISION_CONFLICT",
  "ACTIONS_OUT_OF_ORDER",
  "TICK_AHEAD_OF_SERVER",
  "ILLEGAL_ACTION",
  "DESYNC",
  "STATION_UNAVAILABLE",
  // Back-office
  "INVALID_CREDENTIALS",
  "EMAIL_TAKEN",
  "ADMIN_SELF_ACTION",
  "LAST_ACTIVE_ADMIN",
  "CONFIRMATION_MISMATCH"
];
var IsoDateTimeSchema = z.string();
var PLATFORMS = ["web", "ios", "android"];
var PlatformSchema = z.enum(PLATFORMS);
var CLIENT_PLATFORM_HEADER = "x-client-platform";
var CLIENT_VERSION_HEADER = "x-app-version";
var BOT_CHECK_HEADER = "x-bot-check";
var UPGRADE_REQUIRED_STATUS = 426;
var SemverSchema = z.string().regex(/^\d+\.\d+\.\d+$/, "Version attendue au format x.y.z");
function compareVersions(a, b) {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
function isClientTooOld(version, minimum) {
  if (!version || !SemverSchema.safeParse(version).success) return false;
  return compareVersions(version, minimum) < 0;
}

// src/auth.ts
import { z as z2 } from "zod";
var LOGIN_CODE_LENGTH = 6;
var LOGIN_CODE_TTL_SECONDS = 10 * 60;
var LOGIN_CODE_RESEND_SECONDS = 60;
var LOGIN_CODE_MAX_ATTEMPTS = 5;
var STATION_CHOICE_TTL_SECONDS = 15 * 60;
var EmailSchema = z2.string().trim().toLowerCase().max(254, "Adresse e-mail trop longue").pipe(z2.email("Adresse e-mail invalide"));
var LoginCodeSchema = z2.string().trim().regex(/^\d{6}$/, "Le code fait 6 chiffres");
var PlayerSchema = z2.object({
  id: z2.string(),
  /** Invité (sans e-mail) ou compte (e-mail vérifié). */
  kind: z2.enum(["guest", "account"]),
  email: z2.string().nullable(),
  createdAt: IsoDateTimeSchema
});
var AuthTokensSchema = z2.object({
  accessToken: z2.string(),
  accessTokenExpiresAt: IsoDateTimeSchema,
  refreshToken: z2.string(),
  refreshTokenExpiresAt: IsoDateTimeSchema
});
var AuthSessionResponseSchema = z2.object({
  player: PlayerSchema,
  tokens: AuthTokensSchema.optional()
});
var RefreshRequestSchema = z2.object({
  refreshToken: z2.string().min(1).max(200).optional()
}).default({});
var EmailCodeRequestSchema = z2.object({ email: EmailSchema });
var EmailCodeSentSchema = z2.object({
  resendAfterSeconds: z2.number().int(),
  expiresInSeconds: z2.number().int()
});
var EmailVerifyRequestSchema = z2.object({ email: EmailSchema, code: LoginCodeSchema });
var StationSummarySchema = z2.object({
  credits: z2.number().int(),
  /** En dix-millièmes. */
  reputation: z2.number().int(),
  modules: z2.number().int(),
  /** Secondes de jeu de la station. */
  tick: z2.number().int(),
  createdAt: IsoDateTimeSchema
});
var EmailVerifyResponseSchema = z2.discriminatedUnion("outcome", [
  z2.object({ outcome: z2.literal("secured"), player: PlayerSchema, tokens: AuthTokensSchema.optional() }),
  z2.object({ outcome: z2.literal("signed_in"), player: PlayerSchema, tokens: AuthTokensSchema.optional() }),
  z2.object({
    outcome: z2.literal("choice_required"),
    choiceToken: z2.string(),
    expiresInSeconds: z2.number().int(),
    /** La station de cet appareil (profil invité). */
    current: StationSummarySchema,
    /** La station déjà rattachée à ce compte. */
    account: StationSummarySchema
  })
]);
var StationChoiceRequestSchema = z2.object({
  choiceToken: z2.string().min(1).max(200),
  keep: z2.enum(["current", "account"]),
  confirm: z2.literal(true, "Confirmation requise")
});
var MeResponseSchema = z2.object({ player: PlayerSchema });
var DeleteAccountRequestSchema = z2.object({ confirm: z2.literal(true, "Confirmation requise") });

// src/bot-check.ts
var BOT_CHECK_ACTIONS = ["email_code"];

// src/config.ts
import { z as z3 } from "zod";
var AppSettingsSchema = z3.object({
  /** Bandeau affiché en jeu (`null` : aucun). */
  announcement: z3.string().max(280).nullable(),
  /**
   * Nouvelles stations ouvertes : un compte (et sa station) peut naître à la première connexion par e-mail.
   * Nom historique, gardé pour les clients existants : il ouvrait la création de profils invités, retirée.
   */
  guestSignupsEnabled: z3.boolean(),
  /** Connexion et création de compte par e-mail ouvertes. */
  emailLoginEnabled: z3.boolean()
});
var APP_SETTINGS_DEFAULTS = {
  announcement: null,
  guestSignupsEnabled: true,
  emailLoginEnabled: true
};
var AppConfigResponseSchema = z3.object({
  minClientVersion: z3.string(),
  simVersion: z3.number().int(),
  settings: AppSettingsSchema,
  /** Clé publique Turnstile (`null` : pas de vérification anti-robot). */
  botCheck: z3.object({ siteKey: z3.string().nullable() }),
  serverTime: IsoDateTimeSchema
});

// src/health.ts
import { z as z4 } from "zod";
var HealthResponseSchema = z4.object({
  status: z4.enum(["ok", "error"]),
  database: z4.enum(["up", "down"]),
  version: z4.string(),
  simVersion: z4.number().int(),
  uptimeSeconds: z4.number().int()
});

// src/stations.ts
import { MODULE_TYPES } from "@station-keeping/sim";
import { z as z5 } from "zod";
var SYNC = {
  /** Battement : une synchro au plus tard toutes les 30 secondes. */
  heartbeatSeconds: 30,
  /** Un lot part 1,5 s après la dernière action (tout de suite pour une action importante). */
  debounceMs: 1500,
  /** Avance tolérée sur l'horloge du serveur, en secondes. */
  tickToleranceSeconds: 5,
  /** Un checkpoint absorbe les actions reçues toutes les 60 secondes de jeu. */
  checkpointEveryTicks: 60,
  /** Actions au plus par synchro. */
  maxActionsPerSync: 200
};
var ModuleIdSchema = z5.string().regex(/^m\d{1,9}$/, "Identifiant de module invalide");
var CoordinateSchema = z5.number().int().min(-64).max(64);
var ActionSchema = z5.discriminatedUnion("type", [
  z5.object({
    type: z5.literal("BUILD"),
    module: z5.enum(MODULE_TYPES).exclude(["core"]),
    x: CoordinateSchema,
    y: CoordinateSchema
  }),
  z5.object({ type: z5.literal("DEMOLISH"), moduleId: ModuleIdSchema }),
  z5.object({ type: z5.literal("TOGGLE"), moduleId: ModuleIdSchema, on: z5.boolean() }),
  z5.object({ type: z5.literal("PATCH"), moduleId: ModuleIdSchema }),
  z5.object({ type: z5.literal("REPAIR"), moduleId: ModuleIdSchema }),
  z5.object({ type: z5.literal("UPGRADE"), moduleId: ModuleIdSchema }),
  z5.object({ type: z5.literal("EXTEND_RING") })
]);
var ScheduledActionSchema = z5.object({
  seq: z5.number().int().min(1),
  tick: z5.number().int().min(0),
  action: ActionSchema
});
var StationStateSchema = z5.custom(
  (value) => typeof value === "object" && value !== null && !Array.isArray(value),
  "\xC9tat de station attendu"
);
var OfflineReportSchema = z5.custom(
  (value) => typeof value === "object" && value !== null,
  "R\xE9capitulatif hors ligne attendu"
);
var AnchorSchema = z5.object({ tick: z5.number().int(), serverTime: IsoDateTimeSchema });
var ResyncSchema = z5.object({
  revision: z5.number().int(),
  simVersion: z5.number().int(),
  tick: z5.number().int(),
  hash: z5.string(),
  state: StationStateSchema,
  anchor: AnchorSchema,
  lastSeq: z5.number().int(),
  serverTime: IsoDateTimeSchema
});
var SessionRequestSchema = z5.object({ simVersion: z5.number().int().min(0) });
var SessionResponseSchema = ResyncSchema.extend({
  leaseId: z5.string(),
  /** Récapitulatif « Pendant ton absence », quand l'absence a dépassé le seuil. */
  offlineReport: OfflineReportSchema.nullable()
});
var SyncRequestSchema = z5.object({
  leaseId: z5.uuid(),
  revision: z5.number().int().min(0),
  batchId: z5.uuid(),
  simVersion: z5.number().int().min(0),
  tick: z5.number().int().min(0),
  hash: z5.string().regex(/^[0-9a-f]{16}$/, "Empreinte invalide"),
  actions: z5.array(ScheduledActionSchema).max(SYNC.maxActionsPerSync)
});
var SyncResponseSchema = z5.object({
  revision: z5.number().int(),
  verifiedTick: z5.number().int(),
  serverTime: IsoDateTimeSchema,
  /**
   * Présent quand le client joue avec une autre version de la simulation (app pas encore à jour) : le
   * serveur ne peut pas comparer les empreintes, il renvoie son état à chaque synchro.
   */
  resync: ResyncSchema.optional()
});
var StationSnapshotSchema = z5.object({
  revision: z5.number().int(),
  simVersion: z5.number().int(),
  tick: z5.number().int(),
  hash: z5.string(),
  state: StationStateSchema,
  verifiedAt: IsoDateTimeSchema
});
export {
  API_ERROR_CODES,
  APP_SETTINGS_DEFAULTS,
  ActionSchema,
  AnchorSchema,
  ApiErrorSchema,
  AppConfigResponseSchema,
  AppSettingsSchema,
  AuthSessionResponseSchema,
  AuthTokensSchema,
  BOT_CHECK_ACTIONS,
  BOT_CHECK_HEADER,
  CLIENT_PLATFORM_HEADER,
  CLIENT_VERSION_HEADER,
  DeleteAccountRequestSchema,
  EmailCodeRequestSchema,
  EmailCodeSentSchema,
  EmailSchema,
  EmailVerifyRequestSchema,
  EmailVerifyResponseSchema,
  HealthResponseSchema,
  IsoDateTimeSchema,
  LOGIN_CODE_LENGTH,
  LOGIN_CODE_MAX_ATTEMPTS,
  LOGIN_CODE_RESEND_SECONDS,
  LOGIN_CODE_TTL_SECONDS,
  LoginCodeSchema,
  MeResponseSchema,
  OfflineReportSchema,
  PLATFORMS,
  PlatformSchema,
  PlayerSchema,
  RefreshRequestSchema,
  ResyncSchema,
  STATION_CHOICE_TTL_SECONDS,
  SYNC,
  ScheduledActionSchema,
  SemverSchema,
  SessionRequestSchema,
  SessionResponseSchema,
  StationChoiceRequestSchema,
  StationSnapshotSchema,
  StationStateSchema,
  StationSummarySchema,
  SyncRequestSchema,
  SyncResponseSchema,
  UPGRADE_REQUIRED_STATUS,
  compareVersions,
  isClientTooOld
};
//# sourceMappingURL=index.js.map